import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useDrag } from "@use-gesture/react";
import { X } from "lucide-react";
import * as React from "react";

import { usePortalContainer } from "@src/shared/components/ui/portal";
import { cn } from "@src/shared/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  /**
   * Overrides where this dialog portals. Defaults to the nearest
   * {@link PortalContainerProvider}, then to `document.body`.
   */
  portalContainer?: HTMLElement | null;
  showOverlay?: boolean;
  /**
   * Set to `false` when the dialog renders its own close control, so the
   * built-in absolutely positioned close button does not sit on top of it.
   */
  showCloseButton?: boolean;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      portalContainer,
      showOverlay = true,
      showCloseButton = true,
      ...props
    },
    ref,
  ) => {
    const inheritedPortalContainer = usePortalContainer();

    return (
      <DialogPortal container={portalContainer ?? inheritedPortalContainer}>
        {showOverlay ? <DialogOverlay /> : null}
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
            className,
          )}
          {...props}
        >
          {children}
          {showCloseButton ? (
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

type DraggableDialogContentProps = DialogContentProps & {
  /**
   * Selector for the element within the dialog that starts a drag. Only
   * pointer events on that element move the dialog, so controls elsewhere in
   * the header stay clickable.
   */
  dragHandleSelector?: string;
};

/**
 * Dialog content the user can reposition by dragging a handle inside it.
 *
 * Dragging is bound to the handle element via `@use-gesture`, the same library
 * the floating button uses. Pointer events are the only viable mechanism here:
 * the chrome extension renders this in a content script's shadow root, where a
 * library that reaches for `document`-scoped state or Node globals breaks.
 */
const DraggableDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DraggableDialogContentProps
>(
  (
    {
      className,
      dragHandleSelector = "[data-dialog-drag-handle]",
      style,
      ...props
    },
    forwardedRef,
  ) => {
    const [dragHandle, setDragHandle] = React.useState<HTMLElement | null>(
      null,
    );
    const [offset, setOffset] = React.useState<[number, number]>([0, 0]);

    // Resolving the handle in the ref callback rather than an effect: refs
    // attach bottom-up, so the handle is already in the tree here.
    const setContentRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        setDragHandle(
          node?.querySelector<HTMLElement>(dragHandleSelector) ?? null,
        );

        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [dragHandleSelector, forwardedRef],
    );

    useDrag(
      ({ offset: nextOffset }) => {
        setOffset(nextOffset);
      },
      {
        target: dragHandle ?? undefined,
        enabled: dragHandle != null,
        from: () => offset,
        filterTaps: true,
      },
    );

    return (
      <DialogContent
        ref={setContentRef}
        className={cn("translate-x-0 translate-y-0", className)}
        style={{
          ...style,
          transform: `translate(${offset[0]}px, ${offset[1]}px)`,
        }}
        {...props}
      />
    );
  },
);
DraggableDialogContent.displayName = "DraggableDialogContent";

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  DraggableDialogContent,
};
export type { DialogContentProps, DraggableDialogContentProps };
